import { fr } from "@codegouvfr/react-dsfr";
import Button from "@codegouvfr/react-dsfr/Button";
import { ButtonsGroup } from "@codegouvfr/react-dsfr/ButtonsGroup";
import Highlight from "@codegouvfr/react-dsfr/Highlight";
import Tabs from "@codegouvfr/react-dsfr/Tabs";
import { equals } from "ramda";
import { type ReactNode, useMemo } from "react";
import { HeadingSection, SectionHighlight } from "react-design-system";
import {
  type ConnectedUser,
  domElementIds,
  type EstablishmentDashboardTab,
  establishmentDashboardTabsList,
  frontRoutes,
  immersionFacileHelpdeskRootUrl,
  onlyAdminUserRightsWithStatusAccepted,
  partitionUserEstablishmentRightsByStatus,
  type UserEstablishmentRightDetails,
} from "shared";
import { ConventionTemplatesList } from "src/app/components/agency/agency-dashboard/ConventionTemplatesList";
import { useAppSelector } from "src/app/hooks/reduxHooks";
import { useFeatureFlags } from "src/app/hooks/useFeatureFlags";
import type {
  EstablishmentDashboardRouteName,
  FrontEstablishmentDashboardRoute,
} from "src/app/pages/auth/ConnectedPrivateRoutePage";
import { InitiateConventionButton } from "src/app/pages/establishment-dashboard/InitiateConventionButton";
import { ManageEstablishmentsTab } from "src/app/pages/establishment-dashboard/ManageEstablishmentTab";
import type { DashboardTab } from "src/app/utils/dashboard";
import { commonIllustrations } from "src/assets/img/illustrations";
import { discussionSelectors } from "src/core-logic/domain/discussion/discussion.selectors";
import { initialDiscussionsWithPagination } from "src/core-logic/domain/discussion/discussion.slice";
import { DiscussionTabContent } from "../../discussion/DiscussionTabContent";
import { EstablishmentConventionList } from "./EstablishmentConventionList";

type EstablishmentDashboardTabsProps = {
  currentUser: ConnectedUser;
  route: FrontEstablishmentDashboardRoute;
};

export const EstablishmentDashboardTabs = ({
  currentUser,
  route,
}: EstablishmentDashboardTabsProps): ReactNode => {
  const currentTab = useMemo(
    () => establishmentDashboardTabFromRouteName[route.name],
    [route.name],
  );
  const { data: discussions, filters: discussionsFilters } = useAppSelector(
    discussionSelectors.discussionsWithPagination,
  );
  const filteredDiscussions = discussions.filter(
    (discussions) => !discussions.isEstablishmentBanned,
  );
  const filtersAreEmpty = equals(
    discussionsFilters,
    initialDiscussionsWithPagination.filters,
  );
  const userHasDiscussions = filteredDiscussions.length > 0 || !filtersAreEmpty;

  const tabs = useMemo(
    () =>
      getDashboardTabs(
        makeEstablishmentDashboardTabs(currentUser, userHasDiscussions),
        currentTab,
      ),
    [currentUser, currentTab, userHasDiscussions],
  );
  const { enableEstablishmentDashboardHighlight } = useFeatureFlags();
  const shouldRedirectToMainTab =
    route.name === "establishmentDashboardFormEstablishment" &&
    route.params.siret &&
    !currentUser.establishments?.some(
      (establishment) =>
        establishment.siret === route.params.siret &&
        onlyAdminUserRightsWithStatusAccepted(establishment),
    );

  if (shouldRedirectToMainTab) {
    frontRoutes.establishmentDashboard().push();
    return;
  }

  return (
    <>
      {enableEstablishmentDashboardHighlight.isActive && (
        <SectionHighlight>
          <h2 className={fr.cx("fr-h6", "fr-mb-1w")}>
            {enableEstablishmentDashboardHighlight.value.title}
          </h2>
          <p className={fr.cx("fr-text--lg", "fr-mb-2w")}>
            {enableEstablishmentDashboardHighlight.value.message}
          </p>
          <Button
            size="small"
            linkProps={{
              href: enableEstablishmentDashboardHighlight.value.href,
              target: "_blank",
              rel: "noopener noreferrer",
            }}
          >
            {enableEstablishmentDashboardHighlight.value.label}
          </Button>
        </SectionHighlight>
      )}
      <Tabs
        tabs={tabs}
        id={domElementIds.establishmentDashboard.tabContainer}
        className={fr.cx("fr-mt-4w")}
        selectedTabId={currentTab} // shouldn't be necessary as it's handled by isDefault, but typescript complains (should report to react-dsfr)
        onTabChange={(tab) => {
          if (isEstablishmentDashboardTab(tab)) {
            const routeName = establishmentDashboardRouteNameFromTabId[tab];
            frontRoutes[routeName]().push();
          }
        }}
      >
        {tabs.find((tab) => tab.tabId === currentTab)?.content}
      </Tabs>
    </>
  );
};
const filterActiveEstablishmentsForUser = (
  establishment: UserEstablishmentRightDetails,
) => !establishment.isEstablishmentBanned;

const makeEstablishmentDashboardTabs = (
  {
    dashboards: {
      establishments: { conventions },
    },
    email,
    establishments,
    isBackofficeAdmin,
  }: ConnectedUser,
  userHasDiscussions: boolean,
): DashboardTab[] => {
  const establishmentsArray = establishments
    ? establishments.filter(filterActiveEstablishmentsForUser)
    : [];
  const { acceptedUserEstablishmentsRights, pendingUserEstablishmentsRights } =
    partitionUserEstablishmentRightsByStatus(establishmentsArray);
  const userIsOnboarding = establishmentsArray.length === 0;
  const userCanManageEstablishments = establishmentsArray.length > 0;

  return [
    {
      label: "Conventions",
      tabId: "conventions",
      content: (
        <>
          <HeadingSection
            title="Conventions"
            titleAs="h2"
            titleAction={<InitiateConventionButton />}
            className={fr.cx("fr-mt-0")}
          >
            <EstablishmentConventionList
              conventionsDashboardUrl={conventions}
            />
          </HeadingSection>
          <ConventionTemplatesList
            fromRoute={frontRoutes.establishmentDashboard()}
          />
        </>
      ),
    },
    ...(userHasDiscussions || userIsOnboarding
      ? [
          {
            label: "Candidatures",
            tabId: "discussions",
            content: userHasDiscussions ? (
              <DiscussionTabContent viewer="establishment" />
            ) : (
              <OnboardingTabContent />
            ),
          },
        ]
      : []),
    {
      label: "Mes entreprises",
      tabId: "fiche-entreprise",
      content: userCanManageEstablishments ? (
        <ManageEstablishmentsTab
          establishments={acceptedUserEstablishmentsRights}
          pendingEstablishmentRights={pendingUserEstablishmentsRights}
          currentUserEmail={email}
          isBackofficeAdmin={isBackofficeAdmin}
        />
      ) : (
        <OnboardingTabContent />
      ),
    },
  ];
};

const getDashboardTabs = (
  rawTabs: DashboardTab[],
  currentTab: EstablishmentDashboardTab,
) =>
  rawTabs.map((tab) => ({
    ...tab,
    tabId: tab.tabId,
    isDefault: currentTab === tab.tabId,
  }));

const establishmentDashboardTabFromRouteName: Record<
  EstablishmentDashboardRouteName,
  EstablishmentDashboardTab
> = {
  establishmentDashboard: "conventions",
  establishmentDashboardConventions: "conventions",
  establishmentDashboardFormEstablishment: "fiche-entreprise",
  establishmentDashboardDiscussions: "discussions",
};

const establishmentDashboardRouteNameFromTabId: Record<
  EstablishmentDashboardTab,
  EstablishmentDashboardRouteName
> = {
  conventions: "establishmentDashboard",
  discussions: "establishmentDashboardDiscussions",
  "fiche-entreprise": "establishmentDashboardFormEstablishment",
};

const isEstablishmentDashboardTab = (
  input: string,
): input is EstablishmentDashboardTab =>
  establishmentDashboardTabsList.includes(input as EstablishmentDashboardTab);

const OnboardingTabContent = () => (
  <section className={fr.cx("fr-grid-row", "fr-grid-row--center")}>
    <div className={fr.cx("fr-col-12", "fr-col-lg-7")}>
      <h5 className={fr.cx("fr-h5")}>
        Vous n’êtes rattaché·e à aucune entreprise pour le moment
      </h5>
      <p>Cette situation peut se produire dans les cas suivants :</p>
      <ul>
        <li>l'entreprise n’a pas encore été créée sur Immersion Facilitée,</li>
        <li>
          une autre adresse email est enregistrée comme contact de l'entreprise,
        </li>
      </ul>
      <p>
        Cet espace reste pleinement utilisable pour le suivi des conventions
        auxquelles ce compte est associé.
      </p>
      <Highlight className={fr.cx("fr-ml-0")}>
        <strong>Besoin d’aide ?</strong> N’hésitez pas à{" "}
        <a
          href={immersionFacileHelpdeskRootUrl}
          target="_blank"
          rel="noreferrer"
        >
          contacter le support
        </a>
        ou lire notre article d’aide sur{" "}
        <a
          href={`${immersionFacileHelpdeskRootUrl}/article/comment-referencer-mon-entreprise-en-tant-quentreprise-accueillante-zr6rxv/`}
          target="_blank"
          rel="noreferrer"
        >
          le référencement d’une entreprise
        </a>
        .
      </Highlight>
      <ButtonsGroup
        buttons={[
          {
            id: domElementIds.establishmentDashboard.manageEstablishments
              .createEstablishment,
            priority: "secondary",
            onClick: () => {
              frontRoutes.formEstablishment().push();
            },
            iconId: "fr-icon-add-line",
            children: "Inscrire une nouvelle entreprise",
          },
          {
            id: domElementIds.establishmentDashboard.manageEstablishments
              .registerEstablishmentButton,

            priority: "primary",
            onClick: () => {
              frontRoutes.establishmentUserRegistration().push();
            },
            iconId: "fr-icon-add-line",
            children: "Se rattacher à une entreprise",
          },
        ]}
        inlineLayoutWhen="always"
        className={fr.cx("fr-ml-auto")}
      />
    </div>
    <div
      className={fr.cx(
        "fr-col-12",
        "fr-col-lg-5",
        "fr-hidden",
        "fr-unhidden-lg",
        "fr-px-12w",
        "fr-py-4w",
        "fr-grid-row--middle",
      )}
    >
      <div>
        <img src={commonIllustrations.errorMissing} alt="Accès limité" />
      </div>
    </div>
  </section>
);
